// This device's copy of one show for one account (ADR 0011): the acknowledged snapshot, edits not
// yet acknowledged, edits refused by the server and waiting for review, and open editor drafts.
// Every database is named by account id and show id, so another account or another show on the
// same origin never reads or sends this queue.

import type { ConflictRecord } from "../format/conditional.ts";
import type { Op } from "../format/ops.ts";
import type { AnyRecord, Doc, RecordType } from "../format/types.ts";

export type Role = "owner" | "editor" | "viewer";
export type ShowState = "active" | "archived" | "trashed";

/** Last acknowledged server state; pending edits are always replayed separately. */
export interface ShowSnapshot {
  showId: string;
  accountId: string;
  title: string;
  doc: Doc | null;
  revision: number;
  generation: number;
  state: ShowState;
  role: Role;
  sourceSha256: string;
  savedAt: number;
}

export interface PendingEntry {
  seq?: number;
  /** Idempotency key: the server applies an entry id at most once. */
  id: string;
  /** The tab that made the edit (see sync.ts for adoption of entries from closed tabs). */
  tab: string;
  label: string;
  ops: Op[];
  /** Each op's record as this tab saw it when the edit was made (null: it didn't exist): review context. */
  before: (AnyRecord | null)[];
  at: number;
  /** The show generation the edit was made against; a restore starts a new one. */
  generation: number;
  /** Set when the entry must not be sent until the user chooses to (e.g. kept through sign-out). */
  held?: "signed-out";
}

/** An entry the server refused: kept with what it was based on until the user decides. */
export interface Proposal {
  seq?: number;
  entry: PendingEntry;
  reason: "stale" | "reference" | "invalid" | "restored";
  message: string;
  records: (ConflictRecord & { changedBy?: { name: string; at: number; revision: number } | null })[];
  problems: string[];
  at: number;
}

export interface Draft {
  tab: string;
  at: number;
  state: unknown;
}

/** Kept through sign-out instead of the snapshot: only the records unsent work refers to. */
export interface RecoveryBase {
  title: string;
  revision: number;
  generation: number;
  records: { type: RecordType; key: string; record: AnyRecord | null }[];
  keptAt: number;
}

export const dbName = (accountId: string, showId: string) => `q2q-a.${accountId}.s.${showId}`;

const STORES = ["pending", "proposals", "drafts", "snapshot", "meta"] as const;

let dbPromise: Promise<IDBDatabase> | null = null;
let current = "";

export function openShow(accountId: string, showId: string): void {
  const name = dbName(accountId, showId);
  if (current === name) return;
  void dbPromise?.then((d) => d.close(), () => {});
  current = name;
  dbPromise = null;
}

export function openDb(name: string): Promise<IDBDatabase> {
  const { promise, resolve, reject } = Promise.withResolvers<IDBDatabase>();
  const req = indexedDB.open(name, 1);
  req.onupgradeneeded = () => {
    const d = req.result;
    if (!d.objectStoreNames.contains("pending")) d.createObjectStore("pending", { keyPath: "seq", autoIncrement: true });
    if (!d.objectStoreNames.contains("proposals")) d.createObjectStore("proposals", { keyPath: "seq", autoIncrement: true });
    if (!d.objectStoreNames.contains("drafts")) d.createObjectStore("drafts", { keyPath: "tab" });
    if (!d.objectStoreNames.contains("snapshot")) d.createObjectStore("snapshot");
    if (!d.objectStoreNames.contains("meta")) d.createObjectStore("meta");
  };
  req.onsuccess = () => {
    req.result.onversionchange = () => req.result.close();
    resolve(req.result);
  };
  req.onblocked = () => reject(new Error("Close older q2cue tabs so this device's storage can be upgraded."));
  req.onerror = () => reject(req.error);
  return promise;
}

function db(): Promise<IDBDatabase> {
  if (!current) return Promise.reject(new Error("no show is open"));
  dbPromise ??= openDb(current);
  return dbPromise;
}

export function request<T>(d: IDBDatabase, stores: readonly string[], mode: IDBTransactionMode, fn: (t: IDBTransaction) => IDBRequest<T> | void): Promise<T | undefined> {
  const { promise, resolve, reject } = Promise.withResolvers<T | undefined>();
  const t = d.transaction(stores as string[], mode);
  const req = fn(t);
  t.oncomplete = () => resolve(req ? req.result : undefined);
  t.onerror = () => reject(t.error);
  t.onabort = () => reject(t.error);
  return promise;
}

async function tx<T>(store: (typeof STORES)[number], mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> {
  return request(await db(), [store], mode, (t) => fn(t.objectStore(store)));
}

/** Durably store a pending entry; resolves with its sequence number once the transaction commits. */
export async function addPending(e: PendingEntry): Promise<number> {
  return (await tx("pending", "readwrite", (s) => s.add(e))) as number;
}

export async function allPending(): Promise<PendingEntry[]> {
  return ((await tx("pending", "readonly", (s) => s.getAll())) as PendingEntry[] | undefined) ?? [];
}

export async function putPending(entries: PendingEntry[]): Promise<void> {
  await tx("pending", "readwrite", (s) => {
    for (const e of entries) s.put(e);
  });
}

export async function deletePending(seqs: number[]): Promise<void> {
  await tx("pending", "readwrite", (s) => {
    for (const seq of seqs) s.delete(seq);
  });
}

/** Move refused entries out of the queue and into review, in one transaction. */
export async function moveToProposals(items: Proposal[]): Promise<number[]> {
  const d = await db();
  const seqs: number[] = [];
  await request(d, ["pending", "proposals"], "readwrite", (t) => {
    for (const p of items) {
      if (p.entry.seq !== undefined && p.entry.seq >= 0) t.objectStore("pending").delete(p.entry.seq);
      const { seq: _seq, ...rest } = p;
      t.objectStore("proposals").add(rest).onsuccess = (ev) => seqs.push((ev.target as IDBRequest<number>).result);
    }
  });
  return seqs;
}

export async function allProposals(): Promise<Proposal[]> {
  return ((await tx("proposals", "readonly", (s) => s.getAll())) as Proposal[] | undefined) ?? [];
}

export async function deleteProposal(seq: number): Promise<void> {
  await tx("proposals", "readwrite", (s) => s.delete(seq));
}

/** Commit the acknowledged base and remove acknowledged pending entries in the same transaction. */
export async function saveSnapshot(snapshot: ShowSnapshot, seqs: number[] = []): Promise<void> {
  await request(await db(), ["snapshot", "pending"], "readwrite", (t) => {
    t.objectStore("snapshot").put(snapshot, "show");
    for (const seq of seqs) t.objectStore("pending").delete(seq);
  });
}

export async function readSnapshot(): Promise<ShowSnapshot | undefined> {
  return (await tx("snapshot", "readonly", (s) => s.get("show"))) as ShowSnapshot | undefined;
}

export async function readRecoveryBase(): Promise<RecoveryBase | undefined> {
  return (await tx("meta", "readonly", (s) => s.get("recovery"))) as RecoveryBase | undefined;
}

export async function clearRecoveryBase(): Promise<void> {
  await tx("meta", "readwrite", (s) => s.delete("recovery"));
}

export async function retagPending(seqs: number[], tab: string): Promise<void> {
  const all = await allPending();
  await tx("pending", "readwrite", (s) => {
    for (const e of all) if (e.seq !== undefined && seqs.includes(e.seq)) s.put({ ...e, tab });
  });
}

export async function putDraft(d: Draft): Promise<void> {
  await tx("drafts", "readwrite", (s) => s.put(d));
}

export async function allDrafts(): Promise<Draft[]> {
  return ((await tx("drafts", "readonly", (s) => s.getAll())) as Draft[] | undefined) ?? [];
}

export async function deleteDraft(tab: string): Promise<void> {
  await tx("drafts", "readwrite", (s) => s.delete(tab));
}
